import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { Input, Label, FieldError } from '../ui/Form';
import { BirthDatePicker, BirthTimePicker, SegmentedControl } from '../ui/PremiumControls';
import type { CalculateChartReq } from '@guanwei/shared';

type ZiPolicy = NonNullable<CalculateChartReq['ziPolicy']>;

interface BirthFormProps {
  onSubmit: (req: CalculateChartReq) => void;
  loading?: boolean;
}

export function BirthForm({ onSubmit, loading }: BirthFormProps) {
  const [date, setDate] = useState('1990-08-15');
  const [time, setTime] = useState('14:30');
  const [fuzzyHour, setFuzzyHour] = useState(false);
  const [gender, setGender] = useState<'male' | 'female' | 'unknown'>('male');
  const [birthplace, setBirthplace] = useState('');
  const [longitude, setLongitude] = useState<string>('');
  const [useTrueSolar, setUseTrueSolar] = useState(false);
  const [ziPolicy, setZiPolicy] = useState<ZiPolicy>('apk-default-23');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!date) {
      setError('请填写出生日期');
      return;
    }
    if (!fuzzyHour && !time) {
      setError('请填写出生时间，或勾选"时辰未知"');
      return;
    }

    const req: CalculateChartReq = {
      date,
      time: fuzzyHour ? null : time,
      gender,
      useTrueSolar,
      ziPolicy,
    };
    if (birthplace.trim()) req.birthplace = birthplace.trim();
    const lon = Number(longitude);
    if (longitude && Number.isFinite(lon) && lon > 0) req.longitude = lon;

    onSubmit(req);
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      <div className="rounded-2xl border border-border bg-bg-1 p-5 shadow-sm">
        <p className="font-serif text-xl text-fg">一、建立命盘基准</p>
        <p className="mt-1 text-xs text-fg-muted">
          录入出生日期、时间与性别，用于生成四柱与后续定盘校准。
        </p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <BirthDatePicker
            value={date}
            onChange={setDate}
            required
            error={!date && error ? '请填写出生日期' : undefined}
          />

          <div>
            <BirthTimePicker
              value={time}
              onChange={setTime}
              required={!fuzzyHour}
              disabled={fuzzyHour}
              error={!fuzzyHour && !time && error ? '请填写出生时间，或选择时辰未知' : undefined}
            />
            <button
              type="button"
              onClick={() => {
                setFuzzyHour((v) => {
                  const next = !v;
                  if (next) setUseTrueSolar(false);
                  return next;
                });
              }}
              className={
                'mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors duration-fast ' +
                (fuzzyHour
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border text-fg-2 hover:border-accent hover:text-fg')
              }
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              我不确定具体时辰
            </button>
          </div>

          <div className="sm:col-span-2">
            <SegmentedControl
              label="性别"
              required
              value={gender}
              onChange={setGender}
              options={[
                { value: 'male', label: '男', description: '按男命顺逆规则排运' },
                { value: 'female', label: '女', description: '按女命顺逆规则排运' },
                { value: 'unknown', label: '不指定', description: '仅用于基础命盘展示' },
              ]}
            />
          </div>

          <div className="sm:col-span-2">
            <SegmentedControl
              label="子时换日"
              value={ziPolicy}
              onChange={setZiPolicy}
              options={[
                {
                  value: 'apk-default-23',
                  label: '23点换日',
                  description: '普通子时，23:00 起换日柱',
                },
                {
                  value: 'normal-late-zi',
                  label: '普通晚子',
                  description: '24点换日：日柱用当日，时柱用次日子时',
                },
                {
                  value: 'blind-late-zi-24',
                  label: '盲派24点',
                  description: '24点前日柱和时柱都按当日',
                },
                {
                  value: 'blind-late-zi-2340',
                  label: '盲派23:40',
                  description: '23:40 前按当日，23:40 后按次日',
                },
              ]}
            />
            <p className="mt-2 text-xs text-fg-muted">
              默认采用 23:00 换日柱；如果不知道用哪个，保持默认即可。
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-bg-1 p-5 shadow-sm">
        <p className="font-serif text-xl text-fg">二、校准出生地点</p>
        <p className="mt-1 text-xs text-fg-muted">
          出生地仅在开启真太阳时校正时参与排盘；默认按北京时间排盘。
        </p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="birthplace">出生地（可选）</Label>
            <Input
              id="birthplace"
              type="text"
              value={birthplace}
              onChange={(e) => setBirthplace(e.target.value)}
              placeholder="如：北京 / 上海 / 杭州"
              className="h-12 rounded-lg"
            />
          </div>

          <div>
            <Label htmlFor="longitude">经度（°，东经为正，可选）</Label>
            <Input
              id="longitude"
              type="number"
              step="0.01"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              placeholder="不填则按出生地名称查表"
              className="h-12 rounded-lg"
            />
            <p className="mt-2 text-xs text-fg-muted">
              仅在开启真太阳时时使用；手动经度可用于更精确校正。
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            if (fuzzyHour) return;
            setUseTrueSolar((v) => !v);
          }}
          className={
            'mt-5 flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors duration-fast ' +
            (fuzzyHour
              ? 'border-border bg-bg-2 text-fg-muted opacity-70'
              : useTrueSolar
                ? 'border-accent bg-accent-soft text-accent'
                : 'border-border text-fg-1 hover:border-accent')
          }
        >
          <span>
            <span className="block text-sm font-medium">真太阳时校正</span>
            <span className="mt-1 block text-xs text-fg-muted">
              默认关闭；时辰未知时不可设置真太阳时
            </span>
          </span>
          <span className="text-sm">
            {fuzzyHour ? '不可设置' : useTrueSolar ? '已开启' : '未开启'}
          </span>
        </button>
      </div>

      <FieldError>{error}</FieldError>

      <Button type="submit" size="lg" disabled={loading} className="w-full">
        {loading ? '排盘中…' : '立即排盘'}
      </Button>
    </motion.form>
  );
}
